import React, { ReactElement, useEffect, useState } from 'react'
import {
  NativeSyntheticEvent,
  NativeTouchEvent,
  StyleSheet,
  View,
  Pressable,
  PressableProps,
  ViewStyle,
  TargetedEvent,
  Platform,
  StyleProp,
  ViewProps,
} from 'react-native'

import { Color, Radius, Spacing } from '../../style'
import { Text } from '../typography'

import { Icon } from '../icon'

export interface CheckboxEvent extends NativeSyntheticEvent<NativeTouchEvent> {
  value: boolean
}

interface StyleConfig {
  box?: ViewStyle
}

type Config = {
  default?: StyleConfig
  disabled?: StyleConfig
  pressed?: StyleConfig
  hovered?: StyleConfig
  checked?: StyleConfig
}

const colorConfig: Config = {
  default: StyleSheet.create({
    box: {
      borderColor: Color.calmGrey,
      borderRadius: Radius.S,
      borderWidth: 1,
    },
  }),
  hovered: StyleSheet.create({
    box: {
      borderWidth: 2,
      borderColor: Color.calmGrey,
      height: 18,
      width: 18,
      margin: -1,
    },
  }),
  pressed: StyleSheet.create({
    box: {
      borderWidth: 2,
      borderColor: Color.lightGreyBlue,
      backgroundColor: Color.lightGreyBlue,
    },
  }),
  /*eslint-disable react-native/no-unused-styles */
  checked: StyleSheet.create({
    box: {
      borderColor: Color.greyBlue,
      backgroundColor: Color.greyBlue,
    },
  }),
  /*eslint-enable react-native/no-unused-styles */
}

const styles = StyleSheet.create({
  container: {},
  content: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  box: {
    height: 16,
    width: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  active: {
    borderColor: Color.greyBlue,
    borderWidth: 2,
    width: 18,
    height: 18,
    margin: -1,
  },
  activeChecked: {
    borderColor: Color.lightGreyBlue,
  },
  text: {
    marginLeft: Spacing.XS,
    color: Color.premiumBlack,
  },
  activeText: {
    color: Color.greyBlue,
  },
  skeletonBox: {
    backgroundColor: Color.calmGrey,
  },
})

export interface Props extends Omit<PressableProps, 'onPress'> {
  label?: string
  value?: boolean
  checkmark?: ReactElement
  onChange?: (event: CheckboxEvent) => void
  onValueChange?: (value: boolean) => void
  boxStyle?: StyleProp<ViewStyle>
}
const Checkbox = (props: Props) => {
  const { style, boxStyle, value, checkmark, onChange, onValueChange, label, onFocus, onBlur, ...otherProps } = props
  const [checked, setChecked] = useState(value)
  const [active, setActive] = useState(false)

  useEffect(() => {
    setChecked(value)
  }, [value])

  const updateActive = (value: boolean) => (event: NativeSyntheticEvent<TargetedEvent>) => {
    setActive(value)
    value && onFocus && onFocus(event)
    !value && onBlur && onBlur(event)
  }

  const update = (event: NativeSyntheticEvent<NativeTouchEvent>) => {
    onChange && onChange({ ...event, value: !value })
    onValueChange && onValueChange(!value)
    if (value === undefined) setChecked(!checked)
  }

  const renderCheckbox = (state: { pressed: boolean; hovered?: boolean }) => {
    const { pressed, hovered } = state
    const contentStyle = [
      styles.box,
      colorConfig.default?.box,
      hovered && colorConfig.hovered?.box,
      checked && !pressed && colorConfig.checked?.box,
      active && styles.active,
      active && checked && styles.activeChecked,
      pressed && !checked && colorConfig.pressed?.box,
      boxStyle,
    ]

    const textStyle = [styles.text, active && styles.activeText]
    return (
      <View style={styles.content}>
        <View style={contentStyle}>
          {checked ? checkmark || <Icon name="checkmark" color={Color.simplyWhite} size="XS" /> : undefined}
        </View>
        {!!label && <Text style={textStyle}>{label}</Text>}
      </View>
    )
  }
  return (
    <Pressable
      onPress={update}
      style={[
        styles.container,
        Platform.select({
          // @ts-expect-error outline style none is not defined
          web: {
            outlineStyle: 'none',
          } as ViewStyle,
        }),
        style,
      ]}
      hitSlop={10}
      onFocus={updateActive(true)}
      onBlur={updateActive(false)}
      {...otherProps}
    >
      {renderCheckbox}
    </Pressable>
  )
}

interface SkeletonProps extends ViewProps {
  label?: string
  boxStyle?: StyleProp<ViewStyle>
}
function Skeleton(props: SkeletonProps) {
  const { style, label, boxStyle } = props
  return (
    <View style={[styles.container, styles.skeletonBox, style]}>
      <View style={styles.content}>
        <View style={[styles.box, boxStyle]} />
        {!!label && <Text style={styles.text}>{label}</Text>}
      </View>
    </View>
  )
}
Checkbox.Skeleton = Skeleton

export default Checkbox
